/**
 * Date/time formatting helpers (DESIGN-SYSTEM §3.2 — inline timestamps).
 *
 * Locale-aware via `Intl` for the absolute parts; the relative phrasing is
 * hand-written for the two UI languages (Russian first, the product's primary
 * locale; English mirrors it word for word).
 *
 * IMPORTANT: every `Intl`/`toLocale*` call must pass an explicit locale via
 * {@link appLocale}. A bare `toLocaleString()` inherits the *host* locale, so
 * on a Ukrainian-configured machine/browser month names leak in as "травень"
 * instead of "май". Pinning to the app language keeps output ru/en only.
 */

import i18n from "../i18n";

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

/**
 * BCP-47 locale tag for the app's *current* UI language. Never returns the
 * host locale — that is the whole point (see module note above).
 */
export function appLocale(): string {
  const lng = i18n.language || "ru";
  return lng.startsWith("en") ? "en-US" : "ru-RU";
}

function isEnglish(): boolean {
  return appLocale() === "en-US";
}

/** Coerce a Date | number | ISO-string into a Date. */
function toDate(d: Date | number | string): Date {
  return d instanceof Date ? d : new Date(d);
}

/** Full date + time in the app locale (replaces bare `Date#toLocaleString`). */
export function formatDateTime(d: Date | number | string): string {
  const date = toDate(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(appLocale());
}

/** Date-only in the app locale (replaces bare `Date#toLocaleDateString`). */
export function formatDate(d: Date | number | string): string {
  const date = toDate(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(appLocale());
}

/** Number with locale-aware grouping (replaces bare `Number#toLocaleString`). */
export function formatNumber(n: number): string {
  return n.toLocaleString(appLocale());
}

/** A recording's length: "0:07", "12:30". */
export function formatDuration(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * A file's size: "640 КБ", "1,5 МБ" — never finer than a kilobyte, and a file
 * that has anything in it is never "0".
 */
export function formatFileSize(bytes: number): string {
  const [kb, mb] = isEnglish() ? ["KB", "MB"] : ["КБ", "МБ"];
  if (bytes <= 0) return `0 ${kb}`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} ${kb}`;
  const megabytes = (bytes / (1024 * 1024)).toLocaleString(appLocale(), {
    maximumFractionDigits: 1,
  });
  return `${megabytes} ${mb}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Short timestamp for tables and message lists:
 * - today          → "HH:MM"
 * - yesterday      → "вчера" / "yesterday"
 * - within a year  → "5 мая" (day + month, no year)
 * - older          → locale short date
 */
export function formatShortTime(d: Date | number | string): string {
  const date = toDate(d);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();

  if (isSameDay(date, now)) {
    return date.toLocaleTimeString(appLocale(), { hour: "2-digit", minute: "2-digit" });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) {
    return isEnglish() ? "yesterday" : "вчера";
  }

  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(appLocale(), { day: "numeric", month: "short" });
  }

  return date.toLocaleDateString(appLocale());
}

/**
 * A moment with its hour kept whatever the day: "14:05" today, "13 окт.,
 * 09:00" otherwise. For moments ahead — when a post goes out — where the day
 * alone says too little.
 */
export function formatMoment(d: Date | number | string, timeZone?: string): string {
  const date = toDate(d);
  if (Number.isNaN(date.getTime())) return "";
  // By the clock named, when one is: a schedule runs on the company's clock,
  // and "the next post at 09:00" has to be the 09:00 that was typed into it —
  // not the same moment on the clock of whoever is looking.
  const write = (zone: Intl.DateTimeFormatOptions) => {
    const time = date.toLocaleTimeString(appLocale(), { hour: "2-digit", minute: "2-digit", ...zone });
    const day = (x: Date) => x.toLocaleDateString("en-CA", zone);
    if (day(date) === day(new Date())) return time;
    return `${date.toLocaleDateString(appLocale(), { day: "numeric", month: "short", ...zone })}, ${time}`;
  };
  try {
    return write(timeZone ? { timeZone } : {});
  } catch {
    return write({}); // a zone this browser does not know
  }
}

/** A day without the time: "9 окт." — with the year when it is not this one. */
export function formatShortDate(d: Date | number | string): string {
  const date = toDate(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.getFullYear() === new Date().getFullYear()
    ? date.toLocaleDateString(appLocale(), { day: "numeric", month: "short" })
    : date.toLocaleDateString(appLocale());
}

const RU_PLURAL = new Intl.PluralRules("ru-RU");

function pluralRu(n: number, forms: { one: string; few: string; many: string }): string {
  const cat = RU_PLURAL.select(n);
  if (cat === "one") return forms.one;
  if (cat === "few") return forms.few;
  return forms.many;
}

/**
 * Relative time, in the UI language:
 * - < 1 min  → "только что" / "just now"
 * - minutes  → "2 мин назад" / "2 min ago"
 * - hours    → "1 ч назад" / "1 h ago"
 * - 1 day    → "вчера" / "yesterday"
 * - days     → "3 дня назад" / "3 days ago"
 * - older    → falls back to {@link formatShortTime} (absolute)
 */
export function formatRelative(d: Date | number | string): string {
  const date = toDate(d);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const en = isEnglish();

  // Future or just-now → "только что".
  if (diffMs < MS_PER_MINUTE) return en ? "just now" : "только что";

  if (diffMs < MS_PER_HOUR) {
    const mins = Math.floor(diffMs / MS_PER_MINUTE);
    return en ? `${mins} min ago` : `${mins} мин назад`;
  }

  if (diffMs < MS_PER_DAY) {
    const hours = Math.floor(diffMs / MS_PER_HOUR);
    return en ? `${hours} h ago` : `${hours} ч назад`;
  }

  const days = Math.floor(diffMs / MS_PER_DAY);
  if (days === 1) return en ? "yesterday" : "вчера";
  if (days < 7) {
    if (en) return `${days} days ago`;
    const unit = pluralRu(days, { one: "день", few: "дня", many: "дней" });
    return `${days} ${unit} назад`;
  }

  // Beyond a week, an absolute short date reads better than "14 дней назад".
  return formatShortTime(date);
}
